import Header from '../components/Header';
import Footer from '../components/Footer';
import { Shield, Lock, Eye } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-regal-dark">
      <Header />
      <main className="py-12 md:py-20">
        <div className="container mx-auto px-4">
          <article className="max-w-4xl mx-auto">
            <div className="mb-8">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">
                Privacy Policy
              </h1>
              <p className="text-gray-400">Last updated: December 2, 2025</p>
            </div>
            
            <div className="prose prose-invert max-w-none">
              <div className="bg-regal-gray rounded-xl p-8 mb-8 border border-gray-800">
                <p className="text-xl text-gray-300 leading-relaxed">
                  Regal Play is committed to protecting your privacy and personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website regal-play.com.
                </p>
              </div>

              <div className="space-y-8 text-gray-300 leading-relaxed">
                <section>
                  <div className="flex items-center gap-3 mb-4">
                    <Shield className="w-8 h-8 text-regal-red" />
                    <h2 className="text-3xl font-bold text-white">Information We Collect</h2>
                  </div>
                  <p>
                    We collect information that you provide directly to us when you use our website. This includes personal information such as your name, email address, phone number, and any other information you choose to provide when contacting us through our contact form or subscribing to our newsletter.
                  </p>
                  <p>
                    Automatically collected information includes your IP address, browser type, device information, operating system, access times, and pages viewed. We use cookies and similar tracking technologies to collect this data, which helps us understand how visitors use our website and improve user experience.
                  </p>
                  <p>
                    When you click on affiliate links to casino operators, we may receive referral information from our partners. This information typically includes confirmation that you visited a casino through our link, but does not include sensitive personal data or gambling activity details. We use this information solely for tracking affiliate commissions.
                  </p>
                  <p>
                    We may also collect information from third-party sources, including publicly available information and data from our affiliate partners, to provide you with relevant casino recommendations and to verify the accuracy of information we display on our website.
                  </p>
                </section>

                <section>
                  <div className="flex items-center gap-3 mb-4">
                    <Eye className="w-8 h-8 text-regal-red" />
                    <h2 className="text-3xl font-bold text-white">How We Use Your Information</h2>
                  </div>
                  <p>
                    We use the information we collect to provide, maintain, and improve our services. This includes personalizing your experience, responding to your inquiries, sending you newsletters if you've subscribed, and communicating with you about our services and promotions.
                  </p>
                  <p>
                    Your information helps us analyze website usage patterns, understand user preferences, and optimize our content and layout. We use aggregated, anonymized data for statistical purposes and to improve website functionality and user experience.
                  </p>
                  <p>
                    We may use your contact information to send you important updates about our services, changes to our terms or policies, or security alerts. You can opt out of marketing communications at any time by following the unsubscribe instructions in our emails or contacting us directly.
                  </p>
                  <p>
                    Affiliate tracking information allows us to maintain partnerships with casino operators and receive commissions when users sign up through our links. This information is used solely for business purposes and does not impact the independence of our reviews or recommendations.
                  </p>
                </section>

                <section>
                  <div className="flex items-center gap-3 mb-4">
                    <Lock className="w-8 h-8 text-regal-red" />
                    <h2 className="text-3xl font-bold text-white">Information Sharing and Disclosure</h2>
                  </div>
                  <p>
                    We do not sell, trade, or rent your personal information to third parties. We may share your information with service providers who assist us in operating our website, conducting business, or serving our users, provided these parties agree to keep your information confidential.
                  </p>
                  <p>
                    We may disclose your information if required by law, court order, or governmental authority, or if we believe disclosure is necessary to protect our rights, protect your safety or the safety of others, investigate fraud, or comply with a legal process.
                  </p>
                  <p>
                    When you click on affiliate links to casino operators, you will be redirected to third-party websites that have their own privacy policies. We are not responsible for the privacy practices of these external sites, and we encourage you to review their privacy policies before providing any personal information.
                  </p>
                  <p>
                    In the event of a business transfer, such as a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity. We will notify you of any such change in ownership or control of your personal information.
                  </p>
                </section>

                <section>
                  <h2 className="text-3xl font-bold text-white mb-4">Data Security</h2>
                  <p>
                    We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include encryption, secure servers, access controls, and regular security assessments.
                  </p>
                  <p>
                    However, no method of transmission over the Internet or electronic storage is completely secure. While we strive to use commercially acceptable means to protect your information, we cannot guarantee absolute security. You use our website at your own risk, and we encourage you to take precautions to protect your personal information.
                  </p>
                  <p>
                    We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer need your information, we will securely delete or anonymize it.
                  </p>
                </section>

                <section>
                  <h2 className="text-3xl font-bold text-white mb-4">Your Rights and Choices</h2>
                  <p>
                    Under UK data protection laws, including the Data Protection Act 2018 and UK GDPR, you have certain rights regarding your personal information. You have the right to access, correct, update, or request deletion of your personal information held by us.
                  </p>
                  <p>
                    You can opt out of receiving marketing communications from us at any time by following the unsubscribe link in our emails or contacting us directly. You can also control cookie preferences through your browser settings, though disabling cookies may affect website functionality.
                  </p>
                  <p>
                    You have the right to object to processing of your personal information, request restriction of processing, or request data portability. To exercise these rights, please contact us using the information provided at the end of this policy.
                  </p>
                  <p>
                    If you believe we have processed your personal information in violation of applicable data protection laws, you have the right to lodge a complaint with the UK Information Commissioner's Office (ICO).
                  </p>
                </section>

                <section>
                  <h2 className="text-3xl font-bold text-white mb-4">Cookies and Tracking Technologies</h2>
                  <p>
                    We use cookies and similar tracking technologies to collect and store information about your preferences and interactions with our website. Cookies are small data files stored on your device that help us provide a better user experience.
                  </p>
                  <p>
                    We use both session cookies, which expire when you close your browser, and persistent cookies, which remain on your device until deleted. These cookies help us remember your preferences, analyze website traffic, and track affiliate link clicks for commission purposes.
                  </p>
                  <p>
                    You can control cookie preferences through your browser settings. Most browsers allow you to refuse cookies or alert you when cookies are being sent. However, disabling cookies may limit your ability to use certain features of our website.
                  </p>
                  <p>
                    For more detailed information about our use of cookies, please refer to our Cookie Policy, which provides comprehensive details about the types of cookies we use and how you can manage them.
                  </p>
                </section>

                <section>
                  <h2 className="text-3xl font-bold text-white mb-4">Third-Party Links</h2>
                  <p>
                    Our website contains links to third-party casino operators and other external websites. We are not responsible for the privacy practices, content, or policies of these external sites. We encourage you to read the privacy policies of any third-party sites you visit.
                  </p>
                  <p>
                    When you click on affiliate links to casino operators, you will be directed to their websites, which have their own privacy policies and data collection practices. We recommend reviewing these policies before providing any personal information to casino operators.
                  </p>
                </section>

                <section>
                  <h2 className="text-3xl font-bold text-white mb-4">Children's Privacy</h2>
                  <p>
                    Our website is intended for users who are 18 years of age or older. We do not knowingly collect personal information from individuals under 18 years of age. If we become aware that we have collected information from someone under 18, we will take steps to delete that information promptly.
                  </p>
                  <p>
                    If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately so we can remove that information from our systems.
                  </p>
                </section>

                <section>
                  <h2 className="text-3xl font-bold text-white mb-4">Changes to This Privacy Policy</h2>
                  <p>
                    We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
                  </p>
                  <p>
                    We encourage you to review this Privacy Policy periodically to stay informed about how we collect, use, and protect your information. Your continued use of our website after any changes to this Privacy Policy constitutes your acceptance of the updated terms.
                  </p>
                </section>

                <section>
                  <h2 className="text-3xl font-bold text-white mb-4">Contact Us</h2>
                  <p>
                    If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
                  </p>
                  <div className="bg-regal-gray rounded-lg p-6 border border-gray-800 mt-4">
                    <p className="text-white font-semibold mb-2">Regal Play</p>
                    <p className="text-gray-300">Email: info@regal-play.com</p>
                    <p className="text-gray-300">Phone: +44-0544-403-303</p>
                    <p className="text-gray-300">Address: London, United Kingdom</p>
                  </div>
                </section>
              </div>
            </div>
          </article>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;

